import { CenterItem, Column, Row } from '@components/LinearLayout';

import { P } from '@components/Typography';
import { animated } from '@react-spring/web';
import { EVENT_HOME_WIDTH } from '@utils/constants';
import styled from 'styled-components';

export const FiltersRoot = styled.section<{
  $isOnTop: boolean;
  $navHeightPx: number;
}>`
  position: sticky;
  top: ${({ $navHeightPx }) => `${$navHeightPx + 40}px`};
  z-index: 1;

  display: flex;
  gap: 1rem;
  justify-content: end;
  margin: auto;
  margin-bottom: 4rem;
  width: min(${EVENT_HOME_WIDTH}, 100%);

  &:hover {
    cursor: pointer;
  }

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width768}) {
    justify-content: center;
    top: ${({ $navHeightPx }) => `${$navHeightPx}px`};
  }

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width480}) {
    justify-content: center;
  }
`;

export const FilterBox = styled.div<{
  $isOnTop: boolean;
  $isFiltersApplied: boolean;
  $isTablet: boolean;
  $isSafari: boolean;
}>`
  display: grid;
  place-items: center;
  background-color: ${({ theme }) => theme.color.mobilityGrey};
  border-radius: ${({ theme }) => theme.borderRadius.radius10};
  position: relative;

  /* Width transitions - static on Safari */
  width: ${({ $isOnTop, $isTablet, $isFiltersApplied, $isSafari }) => {
    if ($isSafari) return '100%'; // Always 100% on Safari
    if (!$isOnTop || $isTablet) return '100%';
    return $isFiltersApplied ? '30rem' : '20rem';
  }};
  height: 5rem;

  /* Smooth transitions - disabled on Safari */
  transition: ${({ $isSafari }) =>
    $isSafari ? 'none' : 'width 500ms ease-in-out, height 500ms ease-in-out'};

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width1280}) {
    right: ${({ $isOnTop }) => $isOnTop && '4rem'};
  }
  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width1080}) {
    right: ${({ $isOnTop }) => $isOnTop && '0'};
  }
  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width768}) {
    right: 0;
    margin: ${({ $isOnTop, $isSafari }) =>
      $isSafari ? '0 2rem 0 2rem' : !$isOnTop && '0 2rem 0 2rem'};
    border-radius: ${({ theme, $isOnTop, $isSafari }) =>
      $isSafari
        ? theme.borderRadius.radius10
        : $isOnTop
          ? 0
          : theme.borderRadius.radius10};
  }
`;

export const DateBox = styled.div<{
  $isDateHovered: boolean;
  $isOnTop: boolean;
  $isFirst: boolean;
  $isLast: boolean;
}>`
  display: grid;
  place-items: center;
  background-color: ${({ theme }) => theme.color.mobilityGrey};
  position: relative;
  padding: 0 2rem;
  width: ${({ $isOnTop }) => ($isOnTop ? '100%' : '0')};
  height: 5rem;
  opacity: ${({ $isOnTop }) => ($isOnTop ? 1 : 0)};
  overflow: hidden;

  /* Smooth transitions */
  transition:
    width 600ms ease-in-out,
    height 600ms ease-in-out,
    opacity 600ms ease-in-out;

  display: ${({ $isOnTop }) => !$isOnTop && 'none'};
  ${({ $isFirst, theme }) =>
    $isFirst &&
    `border-top-left-radius: ${theme.borderRadius.radius10};border-bottom-left-radius: ${theme.borderRadius.radius10};`};
  ${({ $isLast, theme }) =>
    $isLast &&
    `border-top-right-radius: ${theme.borderRadius.radius10};border-bottom-right-radius: ${theme.borderRadius.radius10}; z-index: 1;`};
`;

export const DateText = styled(P)<{ $color: string }>`
  color: ${({ theme }) => theme.color.white};
  white-space: nowrap;
  &:hover {
    color: ${({ $color }) => $color};
  }
`;

export const DateBoxWrapper = styled(animated.div)`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

export const FilterText = styled(P)<{ $isHovered: boolean; $color: string }>`
  color: ${({ theme, $isHovered, $color }) =>
    $isHovered ? $color : theme.color.white};
  line-height: ${({ theme }) => theme.lineHeight.height30};
  display: flex;
  align-items: center;
`;

export const FilterDarkText = styled(P)<{
  $color: string;
}>`
  color: ${({ $color }) => $color};
  line-height: ${({ theme }) => theme.lineHeight.height30};
  display: flex;
  align-items: center;
`;

export const FilterModalRoot = styled(Column)`
  height: 100%;
  gap: 5rem;
  padding: 2rem;
  margin-right: 1.25rem;
  margin-top: 1rem;
`;

export const ApplyButtonWrapper = styled(CenterItem)`
  padding: 2rem 0;
  margin: auto;
  box-shadow: 0 -5px 6px -6px rgb(0 0 0 / 0.2);
  width: 100%;
`;

export const ClearFilterWrapper = styled(Row)`
  &:hover {
    cursor: pointer;
  }
`;

export const UnselectedDates = styled.div<{ $isDateHovered: boolean }>`
  display: flex;
  gap: 1rem;
  flex-direction: row;
  transform: ${({ $isDateHovered }) =>
    $isDateHovered ? 'translateX(0rem)' : 'translateX(15rem)'};
  opacity: ${({ $isDateHovered }) => ($isDateHovered ? 1 : 0)};
  transition:
    transform 600ms ease-in-out,
    opacity 600ms ease-in-out;
`;

export const AgendaDates = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin: auto;
`;

export const RotateIcon = styled.div<{ $isDateHovered: boolean }>`
  transform: ${({ $isDateHovered }) =>
    $isDateHovered ? 'rotateZ(-180deg)' : 'rotateZ(0deg)'};
  transition: transform 600ms ease-in-out;
`;

export const DayItem = styled(CenterItem)<{
  $active: boolean;
}>`
  flex: 1 1 10rem;
  width: min(30rem, 100%);
  background-color: ${({ $active, theme }) =>
    $active ? theme.color.mobilityGrey : theme.color.lightGrey2};
  height: 7rem;
  border-radius: ${({ theme }) => theme.borderRadius.radius10};
  &:hover {
    background-color: ${({ theme }) => theme.color.mobilityGrey};
    cursor: pointer;
  }
`;
